// Copyright (c) 2026 杨翔宇（武汉文理学院）
// 物理智能教学智能体平台 (Physics Intelligent Agent)
// 保留所有权利 (All Rights Reserved)。未经著作权人书面许可，
// 不得以任何形式复制、传播、修改或用于商业用途。

type JsonRecord = Record<string, unknown>;

function isRecord(value: unknown): value is JsonRecord {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function toText(value: unknown): string | null {
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed || null;
  }
  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  return null;
}

function formatObjectMessage(value: JsonRecord): string | null {
  const errorText = toText(value.error);
  const messageText = toText(value.message);
  if (errorText && messageText) return `${errorText}: ${messageText}`;
  if (messageText) return messageText;
  if (errorText) return errorText;

  const detailText =
    value.detail !== undefined ? normalizeApiErrorMessagePayload(value.detail, '') : null;
  if (detailText) return detailText;

  return null;
}

function formatDetailList(value: unknown[]): string | null {
  const items = value
    .map((entry) => {
      if (isRecord(entry)) {
        return toText(entry.msg) || toText(entry.message) || formatObjectMessage(entry);
      }
      return toText(entry);
    })
    .filter((entry): entry is string => Boolean(entry));

  if (items.length === 0) return null;
  return items.join('; ');
}

export function normalizeApiErrorMessagePayload(
  payload: unknown,
  fallback = 'Request failed'
): string {
  const directText = toText(payload);
  if (directText) return directText;

  if (Array.isArray(payload)) {
    return formatDetailList(payload) || fallback;
  }

  if (isRecord(payload)) {
    if ('detail' in payload) {
      const detailText = normalizeApiErrorMessagePayload(payload.detail, '');
      if (detailText) return detailText;
    }

    const objectText = formatObjectMessage(payload);
    if (objectText) return objectText;

    try {
      return JSON.stringify(payload);
    } catch {
      return fallback;
    }
  }

  return fallback;
}

export function extractApiErrorMessage(errorLike: unknown, fallback = 'Request failed'): string {
  const errorRecord = isRecord(errorLike) ? errorLike : null;
  const responseRecord =
    errorRecord && isRecord(errorRecord.response) ? errorRecord.response : null;
  const responseData = responseRecord?.data;
  const errorMessage = errorRecord ? toText(errorRecord.message) : null;

  if (responseData !== undefined) {
    return normalizeApiErrorMessagePayload(responseData, errorMessage || fallback);
  }
  return errorMessage || fallback;
}

/**
 * Extract the server-advised retry delay (in seconds) from an API error.
 *
 * Sources, in priority order:
 *   1. the ``Retry-After`` response header (seconds),
 *   2. ``response.data.detail.retry_after`` — the gateway RateLimitMiddleware
 *      429 envelope (``{detail: {error, retry_after, ...}}``),
 *   3. ``response.data.retry_after`` — flat body variant.
 * Returns null when the error carries no usable hint (plain Error, network
 * failure, non-429 responses, unparsable values).
 */
export function extractRetryAfterSeconds(errorLike: unknown): number | null {
  const errorRecord = isRecord(errorLike) ? errorLike : null;
  const responseRecord =
    errorRecord && isRecord(errorRecord.response) ? errorRecord.response : null;
  if (!responseRecord) return null;

  const headers = isRecord(responseRecord.headers) ? responseRecord.headers : null;
  if (headers) {
    const headerValue = toText(headers['retry-after']);
    if (headerValue) {
      const parsed = Number(headerValue);
      if (Number.isFinite(parsed) && parsed > 0) return parsed;
    }
  }

  const responseData = responseRecord.data;
  if (!isRecord(responseData)) return null;

  const detailRecord = isRecord(responseData.detail) ? responseData.detail : null;
  const flatCandidates = [detailRecord?.retry_after, responseData.retry_after];
  for (const candidate of flatCandidates) {
    if (typeof candidate === 'number' && Number.isFinite(candidate) && candidate > 0) {
      return candidate;
    }
  }
  return null;
}

/**
 * Unified user-facing message extraction for any error shape.
 * Handles: AxiosError, Error, string, null/undefined, plain objects.
 * This is the single entry point for extracting display-ready error messages
 * across the frontend — pages and hooks should use this instead of ad-hoc
 * (error as Error).message or scattered console.error.
 */
export function extractUserFacingMessage(error: unknown, fallback = 'Request failed'): string {
  if (error === null || error === undefined) return fallback;
  if (typeof error === 'string') return error.trim() || fallback;
  if (error instanceof Error) return error.message || fallback;
  // Delegate to the existing AxiosError / API error extraction logic
  return extractApiErrorMessage(error, fallback);
}
